import { useHeaderActionsTarget } from '@/layouts/usePageTitle';
import { createPortal } from 'react-dom';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import ColumnValuesFilter from '@/components/common/ColumnValuesFilter';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Download, EllipsisVertical, FileStack, FileText, Info, Plus, Save, Search, Trash2, Wrench, X } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import {
  colorSchemeLightCold,

  themeQuartz,
} from 'ag-grid-community';
import type { ColDef } from 'ag-grid-community';
import {
  createMaintenanceContract,
  deleteMaintenanceContract,
  fetchMaintenanceContractDetail,
  fetchMaintenanceContracts,
  fetchMaintenanceDashboard,
  fetchMaintenanceLookups,
  updateMaintenanceContract,
  type MaintenanceContractDetail,
  type MaintenanceContractLine,
  type MaintenanceContractPayload,
  type MaintenanceContractRow,
  type MaintenanceDashboard,
  type MaintenanceLookups,
} from '@/api/maintenance';
import { fetchPdfBlobUrlByPath } from '@/api/reports';
import { useI18n } from '@/i18n';
import toast from 'react-hot-toast';
import { confirmToast, useErrorToast } from '@/lib/notify';

const modules = [AllEnterpriseModule];

const maintenanceGridTheme = themeQuartz.withPart(colorSchemeLightCold).withParams({
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 11.5,
  rowHeight: 32,
  headerHeight: 32,
  borderRadius: 6,
  headerBackgroundColor: '#f8fafc',
  borderColor: '#e2e8f0',
  oddRowBackgroundColor: '#f8fafc',
});

type FormLine = {
  powerPlantId: number | null;
  powerPlantName: string;
  installedPower: string;
  monthlyFeeUsd: string;
  monthlyFeeTry: string;
  yearlyFeeUsd: string;
  yearlyFeeTry: string;
  vatNote: string;
  notes: string;
};

type FormState = {
  contractKind: string;
  stage: string;
  referenceCode: string;
  investorId: number | null;
  investorNameSnapshot: string;
  contactEmail: string;
  contactPhone: string;
  locationText: string;
  startDate: string;
  durationYears: string;
  endDate: string;
  vatNote: string;
  notes: string;
  lines: FormLine[];
};

const EMPTY_DASHBOARD: MaintenanceDashboard = {
  total_contracts: 0,
  draft_contracts: 0,
  proposal_contracts: 0,
  active_contracts: 0,
  expiring_30_days: 0,
  total_plants: 0,
  monthly_try_total: 0,
  monthly_usd_total: 0,
  yearly_try_total: 0,
  yearly_usd_total: 0,
};

function formatMoney(value: number, currency: 'TRY' | 'USD') {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value ?? 0);
}

function formatCompactMoney(value: number, currency: 'TRY' | 'USD') {
  if (!value) return '';
  return formatMoney(value, currency);
}

function durationLabel(value: number | null) {
  if (!value) return '-';
  return `${value} Yıl`;
}

function stageBadgeClass(stage: string) {
  if (stage === 'active') return 'bg-emerald-100 text-emerald-700';
  if (stage === 'proposal') return 'bg-amber-100 text-amber-700';
  if (stage === 'expired') return 'bg-rose-100 text-rose-700';
  if (stage === 'canceled') return 'bg-slate-200 text-slate-700';
  return 'bg-blue-100 text-blue-700';
}

function createEmptyLine(): FormLine {
  return {
    powerPlantId: null,
    powerPlantName: '',
    installedPower: '',
    monthlyFeeUsd: '',
    monthlyFeeTry: '',
    yearlyFeeUsd: '',
    yearlyFeeTry: '',
    vatNote: '',
    notes: '',
  };
}

function buildInitialForm(detail: MaintenanceContractDetail | null, lookups: MaintenanceLookups | null): FormState {
  if (!detail) {
    return {
      contractKind: lookups?.contractKinds[0]?.id ?? 'monitoring_maintenance',
      stage: 'draft',
      referenceCode: '',
      investorId: null,
      investorNameSnapshot: '',
      contactEmail: '',
      contactPhone: '',
      locationText: '',
      startDate: '',
      durationYears: '',
      endDate: '',
      vatNote: '',
      notes: '',
      lines: [createEmptyLine()],
    };
  }

  return {
    contractKind: detail.contract_kind,
    stage: detail.stage,
    referenceCode: detail.reference_code ?? '',
    investorId: detail.investor_id,
    investorNameSnapshot: detail.investor_name_snapshot,
    contactEmail: detail.contact_email ?? '',
    contactPhone: detail.contact_phone ?? '',
    locationText: detail.location_text ?? '',
    startDate: detail.start_date ?? '',
    durationYears: detail.duration_years ? String(detail.duration_years) : '',
    endDate: detail.end_date ?? '',
    vatNote: detail.vat_note ?? '',
    notes: detail.notes ?? '',
    lines: detail.lines.length > 0
      ? detail.lines.map((line) => ({
          powerPlantId: line.power_plant_id,
          powerPlantName: line.power_plant_name_snapshot,
          installedPower: line.installed_power_text ?? '',
          monthlyFeeUsd: line.monthly_fee_usd ? String(line.monthly_fee_usd) : '',
          monthlyFeeTry: line.monthly_fee_try ? String(line.monthly_fee_try) : '',
          yearlyFeeUsd: line.yearly_fee_usd ? String(line.yearly_fee_usd) : '',
          yearlyFeeTry: line.yearly_fee_try ? String(line.yearly_fee_try) : '',
          vatNote: line.vat_note ?? '',
          notes: line.line_notes ?? '',
        }))
      : [createEmptyLine()],
  };
}

function dateAfterYears(startDate: string, durationYears: string) {
  if (!startDate || !durationYears) return '';
  const years = Number(durationYears);
  if (!Number.isInteger(years) || years < 0) return '';
  const date = new Date(`${startDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  date.setUTCFullYear(date.getUTCFullYear() + years);
  return date.toISOString().slice(0, 10);
}

function buildPayload(form: FormState): MaintenanceContractPayload {
  return {
    contractKind: form.contractKind,
    stage: form.stage,
    referenceCode: form.referenceCode || null,
    investorId: form.investorId,
    investorNameSnapshot: form.investorNameSnapshot,
    contactEmail: form.contactEmail || null,
    contactPhone: form.contactPhone || null,
    locationText: form.locationText || null,
    startDate: form.startDate || null,
    durationYears: form.durationYears ? Number(form.durationYears) : null,
    endDate: form.endDate || null,
    scopeSummary: null,
    vatNote: form.vatNote || null,
    notes: form.notes || null,
    lines: form.lines.map((line, index) => ({
      powerPlantId: line.powerPlantId,
      powerPlantName: line.powerPlantName,
      installedPower: line.installedPower || null,
      monthlyFeeUsd: Number(line.monthlyFeeUsd || 0),
      monthlyFeeTry: Number(line.monthlyFeeTry || 0),
      yearlyFeeUsd: Number(line.yearlyFeeUsd || 0),
      yearlyFeeTry: Number(line.yearlyFeeTry || 0),
      vatNote: line.vatNote || null,
      notes: line.notes || null,
      sortOrder: index,
    })),
  };
}

function sumLineValues(lines: MaintenanceContractLine[] | FormLine[], key: 'yearly_fee_try' | 'yearly_fee_usd' | 'monthly_fee_try' | 'monthly_fee_usd' | 'yearlyFeeTry' | 'yearlyFeeUsd' | 'monthlyFeeTry' | 'monthlyFeeUsd') {
  return lines.reduce((total, line) => total + Number((line as Record<string, unknown>)[key] || 0), 0);
}

function StageLabel({ stage }: { stage: string }) {
  const { t } = useI18n();
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${stageBadgeClass(stage)}`}>
      {t(`maintenance.stage.${stage}`)}
    </span>
  );
}


function PdfPreviewModal({
  title,
  url,
  filename,
  onClose,
}: {
  title: string;
  url: string;
  filename: string;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div ref={ref} className="flex h-[90vh] w-[92vw] max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b bg-slate-50 px-5 py-3">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-slate-800">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={url}
              download={filename}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 transition hover:bg-slate-100"
            >
              <Download className="h-3.5 w-3.5" />
              İndir
            </a>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <iframe src={url} className="h-full w-full flex-1" title={title} />
      </div>
    </div>
  );
}

function ContractModal({
  open,
  detail,
  lookups,
  saving,
  onClose,
  onSubmit,
  onDelete,
}: {
  open: boolean;
  detail: MaintenanceContractDetail | null;
  lookups: MaintenanceLookups | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (payload: MaintenanceContractPayload, uuidId?: string) => Promise<void>;
  onDelete: (uuidId: string) => Promise<void>;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState<FormState>(() => buildInitialForm(detail, lookups));
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);
  const [pdfLoading, setPdfLoading] = useState<'proposal' | 'agreement' | null>(null);
  const [pdfModal, setPdfModal] = useState<{ title: string; filename: string; url: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'contract' | 'plants'>('contract');
  const [actionsOpen, setActionsOpen] = useState(false);

  useEffect(() => {
    setForm(buildInitialForm(detail, lookups));
    setError(null);
    setPdfLoading(null);
    setActiveTab('contract');
    setActionsOpen(false);
    if (pdfModal) {
      URL.revokeObjectURL(pdfModal.url);
      setPdfModal(null);
    }
  }, [detail, lookups, open]);

  const selectedInvestor = useMemo(
    () => lookups?.investors.find((item) => item.id === form.investorId) ?? null,
    [lookups, form.investorId],
  );

  const availablePlants = useMemo(
    () => lookups?.powerPlants.filter((plant) => !form.investorId || plant.investor_id === form.investorId) ?? [],
    [lookups, form.investorId],
  );

  function updateLine(index: number, patch: Partial<FormLine>) {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line)),
    }));
  }

  function addLine() {
    setForm((current) => ({ ...current, lines: [...current.lines, createEmptyLine()] }));
  }

  function removeLine(index: number) {
    setForm((current) => ({
      ...current,
      lines: current.lines.length > 1 ? current.lines.filter((_, lineIndex) => lineIndex !== index) : current.lines,
    }));
  }

  if (!open || !lookups) return null;

  async function handleSave() {
    if (!form.investorNameSnapshot.trim()) {
      setError(t('maintenance.form.requiredInvestor'));
      return;
    }
    if (form.lines.filter((line) => line.powerPlantName.trim()).length === 0) {
      setError(t('maintenance.form.requiredPlant'));
      return;
    }

    setError(null);
    await onSubmit(buildPayload(form), detail?.uuid_id);
  }

  async function handleOpenPdf(kind: 'proposal' | 'agreement') {
    if (!detail?.uuid_id) return;
    setError(null);
    setPdfLoading(kind);
    try {
      const url = await fetchPdfBlobUrlByPath(`/report-api/maintenance/contracts/${detail.uuid_id}/${kind}`);
      setPdfModal({
        title: kind === 'proposal' ? t('maintenance.pdf.proposal') : t('maintenance.pdf.agreement'),
        filename: kind === 'proposal' ? `bakim-teklif-proforma-${detail.uuid_id}.pdf` : `izleme-bakim-sozlesmesi-${detail.uuid_id}.pdf`,
        url,
      });
    } catch (pdfError) {
      setError((pdfError as Error).message || t('maintenance.pdfError'));
    } finally {
      setPdfLoading(null);
    }
  }

  async function handleDelete() {
    if (!detail?.uuid_id) return;

    const confirmed = await confirmToast(t('maintenance.deleteConfirm'), t('maintenance.deleteConfirmMessage'));

    if (!confirmed) return;

    try {
      await onDelete(detail.uuid_id);
      toast.success(t('maintenance.deleteSuccess'));
    } catch (deleteError) {
      setError((deleteError as Error).message || t('maintenance.deleteError'));
      toast.error(t('maintenance.deleteError'));
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
      <div className="relative flex h-[95vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-xl font-semibold text-slate-900">
                  {detail ? t('maintenance.editTitle') : t('maintenance.newTitle')}
                </h2>
                <StageLabel stage={form.stage} />
                {detail?.reference_code && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                    {detail.reference_code}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-slate-500">{t('maintenance.modalSubtitle')}</p>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              {detail?.uuid_id && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setActionsOpen((open) => !open);
                    }}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-violet-200 bg-white text-violet-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-50"
                    aria-label={t('common.edit')}
                  >
                    <EllipsisVertical className="h-4 w-4" />
                  </button>
                  {actionsOpen && (
                    <div
                      className="absolute right-0 top-full z-20 mt-2 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setActionsOpen(false);
                          void handleDelete();
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="h-4 w-4" />
                        {t('common.delete')}
                      </button>
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
                aria-label={t('common.close')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 gap-0 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-h-0 overflow-y-auto px-6 py-5">
            <div className="mb-5 flex items-center gap-2 border-b border-slate-200">
              <button
                type="button"
                onClick={() => setActiveTab('contract')}
                className={`border-b-2 px-1 py-3 text-sm font-medium transition ${activeTab === 'contract' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              >
                {t('maintenance.tabs.contract')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('plants')}
                className={`border-b-2 px-1 py-3 text-sm font-medium transition ${activeTab === 'plants' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              >
                {t('maintenance.tabs.plants')}
              </button>
            </div>

            {activeTab === 'contract' ? (
            <div className="grid gap-5 lg:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('maintenance.fields.contractKind')}</span>
                <select
                  value={form.contractKind}
                  onChange={(e) => setForm((current) => ({ ...current, contractKind: e.target.value }))}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                >
                  {lookups.contractKinds.map((option) => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('maintenance.fields.stage')}</span>
                <select
                  value={form.stage}
                  onChange={(e) => setForm((current) => ({ ...current, stage: e.target.value }))}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                >
                  {lookups.stages.map((option) => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('maintenance.fields.investor')}</span>
                <select
                  value={form.investorId ?? ''}
                  onChange={(e) => {
                    const nextId = e.target.value ? Number(e.target.value) : null;
                    const investor = lookups.investors.find((item) => item.id === nextId) ?? null;
                    setForm((current) => ({
                      ...current,
                      investorId: nextId,
                      investorNameSnapshot: investor?.name ?? current.investorNameSnapshot,
                      contactEmail: investor?.email ?? current.contactEmail,
                      contactPhone: investor?.phone ?? current.contactPhone,
                      locationText: investor?.address ?? current.locationText,
                    }));
                  }}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                >
                  <option value="">{t('maintenance.filters.allInvestors')}</option>
                  {lookups.investors.map((investor) => (
                    <option key={investor.id} value={investor.id}>{investor.name}</option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('maintenance.fields.referenceCode')}</span>
                <input
                  value={form.referenceCode}
                  onChange={(e) => setForm((current) => ({ ...current, referenceCode: e.target.value }))}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                  placeholder="SVC-2026-12"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('maintenance.fields.investorName')}</span>
                <input
                  value={form.investorNameSnapshot}
                  onChange={(e) => setForm((current) => ({ ...current, investorNameSnapshot: e.target.value }))}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('maintenance.fields.location')}</span>
                <input
                  value={form.locationText}
                  onChange={(e) => setForm((current) => ({ ...current, locationText: e.target.value }))}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('maintenance.fields.email')}</span>
                <input
                  value={form.contactEmail}
                  onChange={(e) => setForm((current) => ({ ...current, contactEmail: e.target.value }))}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('maintenance.fields.phone')}</span>
                <input
                  value={form.contactPhone}
                  onChange={(e) => setForm((current) => ({ ...current, contactPhone: e.target.value }))}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('maintenance.fields.startDate')}</span>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => {
                    const nextStartDate = e.target.value;
                    setForm((current) => ({
                      ...current,
                      startDate: nextStartDate,
                      endDate: dateAfterYears(nextStartDate, current.durationYears),
                    }));
                  }}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('maintenance.fields.durationYears')}</span>
                <input
                  type="number"
                  min={0}
                  value={form.durationYears}
                  onChange={(e) => {
                    const nextDuration = e.target.value;
                    setForm((current) => ({
                      ...current,
                      durationYears: nextDuration,
                      endDate: dateAfterYears(current.startDate, nextDuration),
                    }));
                  }}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('maintenance.fields.endDate')}</span>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm((current) => ({ ...current, endDate: e.target.value }))}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">{t('maintenance.fields.vat')}</span>
                <input
                  value={form.vatNote}
                  onChange={(e) => setForm((current) => ({ ...current, vatNote: e.target.value }))}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                  placeholder="%20 KDV Dahil"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm lg:col-span-2">
                <span className="font-medium text-slate-700">{t('maintenance.fields.notes')}</span>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((current) => ({ ...current, notes: e.target.value }))}
                  rows={3}
                  className="rounded-2xl border border-slate-200 px-3 py-2"
                />
              </label>
            </div>
            ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{t('maintenance.linesTitle')}</h3>
                  <p className="text-sm text-slate-500">{t('maintenance.linesHint')}</p>
                </div>
                <button
                  type="button"
                  onClick={addLine}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Plus className="h-4 w-4" />
                  {t('maintenance.addLine')}
                </button>
              </div>

              <div className="space-y-4">
                {form.lines.map((line, index) => (
                  <div key={`${index}-${line.powerPlantId ?? 'new'}`} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <div className="mb-4 flex items-center justify-between">
                      <div className="text-sm font-semibold text-slate-800">
                        {t('maintenance.lineLabel', { index: index + 1 })}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={addLine}
                          className="inline-flex items-center justify-center rounded-md border border-slate-200 p-1 text-slate-600 hover:bg-white"
                          aria-label={t('maintenance.addLine')}
                          title={t('maintenance.addLine')}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeLine(index)}
                          disabled={form.lines.length <= 1}
                          className="inline-flex items-center justify-center rounded-md border border-rose-200 p-1 text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label={t('common.delete')}
                          title={t('common.delete')}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                      <label className="flex flex-col gap-1 text-sm">
                        <span className="font-medium text-slate-700">{t('maintenance.fields.powerPlant')}</span>
                        <select
                          value={line.powerPlantId ?? ''}
                          onChange={(e) => {
                            const nextId = e.target.value ? Number(e.target.value) : null;
                            const plant = availablePlants.find((item) => item.id === nextId) ?? null;
                            updateLine(index, {
                              powerPlantId: nextId,
                              powerPlantName: plant?.name ?? line.powerPlantName,
                            });
                          }}
                          className="w-full rounded-lg border border-slate-200 px-2 py-1.5"
                        >
                          <option value="">{t('maintenance.form.selectPowerPlant')}</option>
                          {availablePlants.map((plant) => (
                            <option key={plant.id} value={plant.id}>{plant.name}</option>
                          ))}
                        </select>
                      </label>

                      <label className="flex flex-col gap-1 text-sm">
                        <span className="font-medium text-slate-700">{t('maintenance.fields.powerPlantName')}</span>
                        <input
                          value={line.powerPlantName}
                          onChange={(e) => updateLine(index, { powerPlantName: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 px-2 py-1.5"
                        />
                      </label>

                      <label className="flex flex-col gap-1 text-sm">
                        <span className="font-medium text-slate-700">{t('maintenance.fields.installedPower')}</span>
                        <input
                          value={line.installedPower}
                          onChange={(e) => updateLine(index, { installedPower: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 px-2 py-1.5"
                          placeholder="4.396,425 kWp"
                        />
                      </label>

                      <label className="flex flex-col gap-1 text-sm">
                        <span className="font-medium text-slate-700">{t('maintenance.fields.lineVat')}</span>
                        <input
                          value={line.vatNote}
                          onChange={(e) => updateLine(index, { vatNote: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 px-2 py-1.5"
                        />
                      </label>

                      <label className="flex flex-col gap-1 text-sm">
                        <span className="font-medium text-slate-700">{t('maintenance.fields.monthlyUsd')}</span>
                        <input
                          type="number"
                          step="0.01"
                          value={line.monthlyFeeUsd}
                          onChange={(e) => updateLine(index, { monthlyFeeUsd: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 px-2 py-1.5"
                        />
                      </label>

                      <label className="flex flex-col gap-1 text-sm">
                        <span className="font-medium text-slate-700">{t('maintenance.fields.monthlyTry')}</span>
                        <input
                          type="number"
                          step="0.01"
                          value={line.monthlyFeeTry}
                          onChange={(e) => updateLine(index, { monthlyFeeTry: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 px-2 py-1.5"
                        />
                      </label>

                      <label className="flex flex-col gap-1 text-sm">
                        <span className="font-medium text-slate-700">{t('maintenance.fields.yearlyUsd')}</span>
                        <input
                          type="number"
                          step="0.01"
                          value={line.yearlyFeeUsd}
                          onChange={(e) => updateLine(index, { yearlyFeeUsd: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 px-2 py-1.5"
                        />
                      </label>

                      <label className="flex flex-col gap-1 text-sm">
                        <span className="font-medium text-slate-700">{t('maintenance.fields.yearlyTry')}</span>
                        <input
                          type="number"
                          step="0.01"
                          value={line.yearlyFeeTry}
                          onChange={(e) => updateLine(index, { yearlyFeeTry: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 px-2 py-1.5"
                        />
                      </label>

                      <label className="flex flex-col gap-1 text-sm lg:col-span-2">
                        <span className="font-medium text-slate-700">{t('maintenance.fields.lineNotes')}</span>
                        <textarea
                          rows={2}
                          value={line.notes}
                          onChange={(e) => updateLine(index, { notes: e.target.value })}
                          className="w-full rounded-2xl border border-slate-200 px-3 py-2"
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>

          <aside className="flex min-h-0 flex-col border-l border-slate-200 bg-slate-50">
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
              <div className="space-y-5">
              <section className="rounded-2xl bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-slate-900">{t('maintenance.pdfActionsTitle')}</h3>
                </div>
                <div className="space-y-2">
                  <button
                    type="button"
                    disabled={!detail?.uuid_id || pdfLoading !== null}
                    onClick={() => handleOpenPdf('proposal')}
                    className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      {t('maintenance.pdf.proposal')}
                    </span>
                    {pdfLoading === 'proposal' ? t('common.loading') : t('maintenance.pdf.open')}
                  </button>
                  <button
                    type="button"
                    disabled={!detail?.uuid_id || pdfLoading !== null}
                    onClick={() => handleOpenPdf('agreement')}
                    className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      {t('maintenance.pdf.agreement')}
                    </span>
                    {pdfLoading === 'agreement' ? t('common.loading') : t('maintenance.pdf.open')}
                  </button>
                </div>
              </section>

              <section className="rounded-2xl bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-900">{t('maintenance.summaryTitle')}</h3>
                <div className="mt-3 space-y-2 text-sm text-slate-600">
                  <div className="flex items-center justify-between">
                    <span>{t('maintenance.summary.selectedInvestor')}</span>
                    <span className="font-medium text-slate-900">{selectedInvestor?.name ?? form.investorNameSnapshot ?? '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>{t('maintenance.summary.plantCount')}</span>
                    <span className="font-medium text-slate-900">{form.lines.filter((line) => line.powerPlantName.trim()).length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>{t('maintenance.summary.yearlyTry')}</span>
                    <span className="font-medium text-slate-900">{formatMoney(sumLineValues(form.lines, 'yearlyFeeTry'), 'TRY')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>{t('maintenance.summary.yearlyUsd')}</span>
                    <span className="font-medium text-slate-900">{formatMoney(sumLineValues(form.lines, 'yearlyFeeUsd'), 'USD')}</span>
                  </div>
                </div>
              </section>

              <section className="rounded-2xl bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-900">{t('maintenance.documentsTitle')}</h3>
                {detail?.documents.length ? (
                  <div className="mt-3 space-y-2">
                    {detail.documents.map((document) => (
                      <div key={document.id} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                        <div className="font-medium text-slate-800">{document.title}</div>
                        <div className="text-xs text-slate-500">{document.document_kind} · {document.created_on}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-slate-500">{t('maintenance.documentsEmpty')}</p>
                )}
              </section>

              <section className="rounded-2xl bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-900">{t('maintenance.historyTitle')}</h3>
                {detail?.history.length ? (
                  <div className="mt-3 space-y-2">
                    {detail.history.map((item) => (
                      <div key={item.id} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                        <div className="font-medium text-slate-800">{item.details || item.event_type}</div>
                        <div className="text-xs text-slate-500">{item.changed_by_name ?? '-'} · {item.created_on}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-slate-500">{t('maintenance.historyEmpty')}</p>
                )}
              </section>

              </div>
            </div>

            <div className="border-t border-slate-200 bg-slate-50 px-5 py-4">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Save className="h-4 w-4" />
                  {saving ? t('maintenance.saving') : t('common.save')}
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
      {pdfModal && (
        <PdfPreviewModal
          title={pdfModal.title}
          url={pdfModal.url}
          filename={pdfModal.filename}
          onClose={() => {
            URL.revokeObjectURL(pdfModal.url);
            setPdfModal(null);
          }}
        />
      )}
    </div>
  );
}

function SummaryInfo({ label, text }: { label: string; text: string }) {
  const id = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const bounds = buttonRef.current?.getBoundingClientRect();
  const [open, setOpen] = useState(false);
  return <span className="relative inline-flex" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
    <button ref={buttonRef} type="button" aria-label={`${label} hesaplama bilgisi`} aria-describedby={open ? id : undefined}
      className="rounded text-slate-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
      onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onClick={() => setOpen(true)} onKeyDown={event => { if (event.key === 'Escape') setOpen(false); }}>
      <Info size={13} />
    </button>
    {open && bounds && createPortal(<span id={id} role="tooltip" style={{ top: bounds.bottom + 8, left: Math.max(8, Math.min(bounds.right - 240, window.innerWidth - 248)) }} className="pointer-events-none fixed z-[100] w-60 rounded-lg bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-5 text-white shadow-lg">{text}</span>, document.body)}
  </span>;
}

export default function MaintenancePage() {
  const headerActionsTarget = useHeaderActionsTarget();
  const { t } = useI18n();
  const [lookups, setLookups] = useState<MaintenanceLookups | null>(null);
  const [dashboard, setDashboard] = useState<MaintenanceDashboard>(EMPTY_DASHBOARD);
  const [rows, setRows] = useState<MaintenanceContractRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const rateExplanation = dashboard.exchange_rate
    ? `TCMB USD döviz satış kuru: ${dashboard.exchange_rate.rate.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} TL. Kur tarihi: ${dashboard.exchange_rate.date}.${dashboard.exchange_rate.stale ? ' Kur yenilenemedi; son alınan kur kullanılıyor.' : ''}`
    : 'TCMB kuru alınamadığı için birleşik toplam gösterilemiyor.';


  const colDefs = useMemo<ColDef<MaintenanceContractRow>[]>(
    () => [
      {
        field: 'investor_name_snapshot',
        headerName: t('maintenance.table.investor'),
        minWidth: 140,
        width: 150,
        pinned: 'left',
      },
      {
        colId: 'contact',
        headerName: t('maintenance.table.email'),
        minWidth: 130,
        width: 140,
        valueGetter: ({ data }) => data?.contact_email || data?.contact_phone || '-',
      },
      {
        field: 'plant_names',
        headerName: t('maintenance.table.plants'),
        minWidth: 170,
        width: 180,
        cellRenderer: ({ data }: { data?: MaintenanceContractRow }) => (
          <div className="py-0.5 leading-tight">
            <div className="truncate text-[11px] text-slate-800" title={data?.plant_names || '-'}>
              {data?.plant_names || '-'}
            </div>
            <div className="mt-0.5 text-[10px] text-slate-500">{t('maintenance.table.plantCount', { count: data?.line_count ?? 0 })}</div>
          </div>
        ),
      },
      {
        field: 'monthly_fee_usd_total',
        aggFunc: 'sum',
        type: 'numericColumn',
        cellDataType: 'number',
        cellClass: 'ag-right-aligned-cell tabular-nums',
        headerClass: 'ag-right-aligned-header',
        valueGetter: ({ data }) => data?.monthly_fee_usd_total == null ? null : Number(data.monthly_fee_usd_total),
        filterParams: { valueLabel: (value: unknown) => formatCompactMoney(Number(value || 0), 'USD') || '-' },
        headerName: t('maintenance.table.monthlyUsd'),
        minWidth: 110,
        width: 120,
        valueFormatter: ({ value }) => formatCompactMoney(Number(value || 0), 'USD') || '-',
      },
      {
        field: 'monthly_fee_try_total',
        aggFunc: 'sum',
        type: 'numericColumn',
        cellDataType: 'number',
        cellClass: 'ag-right-aligned-cell tabular-nums',
        headerClass: 'ag-right-aligned-header',
        valueGetter: ({ data }) => data?.monthly_fee_try_total == null ? null : Number(data.monthly_fee_try_total),
        filterParams: { valueLabel: (value: unknown) => formatCompactMoney(Number(value || 0), 'TRY') || '-' },
        headerName: t('maintenance.table.monthlyTry'),
        minWidth: 110,
        width: 120,
        valueFormatter: ({ value }) => formatCompactMoney(Number(value || 0), 'TRY') || '-',
      },
      {
        field: 'yearly_fee_usd_total',
        aggFunc: 'sum',
        type: 'numericColumn',
        cellDataType: 'number',
        cellClass: 'ag-right-aligned-cell tabular-nums',
        headerClass: 'ag-right-aligned-header',
        valueGetter: ({ data }) => data?.yearly_fee_usd_total == null ? null : Number(data.yearly_fee_usd_total),
        filterParams: { valueLabel: (value: unknown) => formatCompactMoney(Number(value || 0), 'USD') || '-' },
        headerName: t('maintenance.table.yearlyUsd'),
        minWidth: 110,
        width: 120,
        valueFormatter: ({ value }) => formatCompactMoney(Number(value || 0), 'USD') || '-',
      },
      {
        field: 'yearly_fee_try_total',
        aggFunc: 'sum',
        type: 'numericColumn',
        cellDataType: 'number',
        cellClass: 'ag-right-aligned-cell tabular-nums',
        headerClass: 'ag-right-aligned-header',
        valueGetter: ({ data }) => data?.yearly_fee_try_total == null ? null : Number(data.yearly_fee_try_total),
        filterParams: { valueLabel: (value: unknown) => formatCompactMoney(Number(value || 0), 'TRY') || '-' },
        headerName: t('maintenance.table.yearlyTry'),
        minWidth: 110,
        width: 120,
        valueFormatter: ({ value }) => formatCompactMoney(Number(value || 0), 'TRY') || '-',
      },
      { field: 'start_date', headerName: t('maintenance.table.startDate'), minWidth: 105, width: 110, valueFormatter: ({ value }) => value || '-' },
      {
        field: 'duration_years',
        headerName: t('maintenance.table.durationYears'),
        minWidth: 90,
        width: 95,
        valueFormatter: ({ value }) => durationLabel(value ?? null),
      },
      { field: 'end_date', headerName: t('maintenance.table.endDate'), minWidth: 105, width: 110, valueFormatter: ({ value }) => value || '-' },
      {
        field: 'scope_display',
        headerName: t('maintenance.table.scope'),
        minWidth: 90,
        width: 100,
        cellRenderer: ({ value }: { value?: string | null }) => (
          <span className="inline-flex rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            {value || '-'}
          </span>
        ),
      },
      { field: 'installed_power_texts', headerName: t('maintenance.table.installedPower'), minWidth: 140, width: 150, valueFormatter: ({ value }) => value || '-' },
      { field: 'notes', headerName: t('maintenance.table.notes'), minWidth: 140, width: 150, valueFormatter: ({ value }) => value || '-' },
      {
        field: 'stage',
        filterParams: { valueLabel: (value: unknown) => lookups?.stages.find(option => option.id === value)?.name || String(value ?? '-') },
        headerName: t('maintenance.table.stage'),
        minWidth: 180,
        width: 200,
        cellRenderer: ({ data }: { data?: MaintenanceContractRow }) => (
          <div className="flex h-full items-center gap-2 whitespace-nowrap">
            <div><StageLabel stage={data?.stage || 'draft'} /></div>
            {data?.remaining_days !== null && data?.remaining_days !== undefined && (
              <span className={data.remaining_days < 0 ? 'text-[10px] font-medium text-rose-600' : data.remaining_days <= 30 ? 'text-[10px] font-medium text-amber-600' : 'text-[10px] text-slate-600'}>
                {data.remaining_days < 0
                  ? t('maintenance.remaining.overdue', { count: Math.abs(data.remaining_days) })
                  : t('maintenance.remaining.left', { count: data.remaining_days })}
              </span>
            )}
          </div>
        ),
      },
    ],
    [t, lookups?.stages],
  );

  const defaultColDef = useMemo<ColDef<MaintenanceContractRow>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: ColumnValuesFilter,
      floatingFilter: false,
      suppressHeaderMenuButton: false,
      headerClass: 'maintenance-grid-header',
      cellRendererSelector: params => params.node.footer ? {
        component: () => params.colDef?.field === 'investor_name_snapshot'
          ? 'Toplam'
          : params.colDef?.aggFunc === 'sum' ? (params.valueFormatted ?? params.value ?? '') : '',
      } : undefined,
    }),
    [],
  );
  const [activeDetail, setActiveDetail] = useState<MaintenanceContractDetail | null>(null);


  async function loadStatic() {
    const [lookupsData, dashboardData] = await Promise.all([
      fetchMaintenanceLookups(),
      fetchMaintenanceDashboard(),
    ]);
    setLookups(lookupsData);
    setDashboard(dashboardData);
  }

  async function loadContracts(currentStage = stageFilter, currentSearch = search) {
    const list = await fetchMaintenanceContracts({
      stage: currentStage || undefined,
      q: currentSearch.trim() || undefined,
    });
    setRows(list);
  }

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([loadStatic(), loadContracts('')])
      .catch(() => setError(t('maintenance.loadError')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadContracts().catch(() => setError(t('maintenance.loadError')));
    }, 200);
    return () => window.clearTimeout(timer);
  }, [stageFilter, search, t]);

  async function openCreate() {
    setActiveDetail(null);
    setModalOpen(true);
  }

  async function openEdit(uuidId: string) {
    setDetailLoading(true);
    setModalOpen(true);
    try {
      const detail = await fetchMaintenanceContractDetail(uuidId);
      setActiveDetail(detail);
    } catch {
      setError(t('maintenance.detailLoadError'));
      setModalOpen(false);
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleSubmit(payload: MaintenanceContractPayload, uuidId?: string) {
    setSaving(true);
    setError(null);
    try {
      const saved = uuidId
        ? await updateMaintenanceContract(uuidId, payload)
        : await createMaintenanceContract(payload);
      setActiveDetail(saved);
      await Promise.all([loadStatic(), loadContracts()]);
      setModalOpen(false);
    } catch (submitError) {
      setError((submitError as Error).message || t('maintenance.saveError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(uuidId: string) {
    setSaving(true);
    setError(null);
    try {
      await deleteMaintenanceContract(uuidId);
      setModalOpen(false);
      setActiveDetail(null);
      await Promise.all([loadStatic(), loadContracts()]);
    } catch (deleteError) {
      setError((deleteError as Error).message || t('maintenance.deleteError'));
      throw deleteError;
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {headerActionsTarget && createPortal(<div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <label className="flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-2 text-slate-400">
              <Search size={14} className="shrink-0" />
              <input type="search" aria-label="Bakım sözleşmelerinde ara" value={search} onChange={event => setSearch(event.target.value)} placeholder={t('maintenance.filters.searchPlaceholder')} className="w-32 min-w-0 bg-transparent text-xs text-slate-700 outline-none xl:w-40" />
            </label>
            <div role="group" aria-label="Sözleşme aşaması" className="flex flex-wrap items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
              {[{ id: '', name: 'Tümü' }, ...(lookups?.stages || [])].map(option => (
                <button key={option.id} type="button" aria-pressed={stageFilter === option.id} onClick={() => setStageFilter(option.id)}
                  className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${stageFilter === option.id ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}>
                  {option.name}
                </button>
              ))}
            </div>
          </div>
          <button type="button" onClick={openCreate} className="inline-flex items-center gap-1.5 rounded-lg h-8 bg-blue-600 px-3 text-xs font-medium text-white transition hover:bg-blue-700">
            <Plus size={14} />Yeni
          </button>
        </div>,headerActionsTarget)}

      <section aria-label="Bakım sözleşmeleri" className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-4 grid shrink-0 grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-7">
        <div className="rounded-lg bg-white px-2.5 py-2 ring-1 ring-slate-200">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500">{t('maintenance.cards.totalContracts')}<SummaryInfo label={t('maintenance.cards.totalContracts')} text="Tüm aşamalardaki sözleşmelerin toplam sayısıdır." /></div>
              <div className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900">{dashboard.total_contracts}</div>
            </div>
            <FileStack className="h-4 w-4 shrink-0 text-slate-400" />
          </div>
        </div>
        <div className="rounded-lg bg-white px-2.5 py-2 ring-1 ring-slate-200">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500">{t('maintenance.cards.activeContracts')}<SummaryInfo label={t('maintenance.cards.activeContracts')} text="Aktif aşamasında olan ve bitiş tarihi geçmemiş sözleşmelerin sayısıdır. Bitiş günü dahildir." /></div>
              <div className="mt-0.5 text-sm font-semibold tabular-nums text-emerald-700">{dashboard.active_contracts}</div>
            </div>
            <Wrench className="h-4 w-4 shrink-0 text-emerald-400" />
          </div>
        </div>
        <div className="rounded-lg bg-white px-2.5 py-2 ring-1 ring-slate-200">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500">{t('maintenance.cards.expiringSoon')}<SummaryInfo label={t('maintenance.cards.expiringSoon')} text="Aktif sözleşmelerden önümüzdeki 30 gün içinde bitecek olanların sayısıdır." /></div>
              <div className="mt-0.5 text-sm font-semibold tabular-nums text-amber-700">{dashboard.expiring_30_days}</div>
            </div>
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
          </div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2">
          <div className="flex items-center justify-between gap-1 text-[11px] text-amber-700">{t('maintenance.cards.monthlyTotalUsd')}<SummaryInfo label={t('maintenance.cards.monthlyTotalUsd')} text={`Aylık toplam: USD bedelleri + (TL bedelleri ÷ TCMB USD satış kuru). ${rateExplanation}`} /></div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-amber-800">{dashboard.converted_monthly_usd_total == null ? '—' : formatMoney(dashboard.converted_monthly_usd_total, 'USD')}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-2">
          <div className="flex items-center justify-between gap-1 text-[11px] text-emerald-700">{t('maintenance.cards.monthlyTotalTry')}<SummaryInfo label={t('maintenance.cards.monthlyTotalTry')} text={`Aylık toplam: TL bedelleri + (USD bedelleri × TCMB USD satış kuru). ${rateExplanation}`} /></div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-emerald-800">{dashboard.converted_monthly_try_total == null ? '—' : formatMoney(dashboard.converted_monthly_try_total, 'TRY')}</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2">
          <div className="flex items-center justify-between gap-1 text-[11px] text-amber-700">{t('maintenance.cards.yearlyTotalUsd')}<SummaryInfo label={t('maintenance.cards.yearlyTotalUsd')} text={`Yıllık toplam: USD bedelleri + (TL bedelleri ÷ TCMB USD satış kuru). ${rateExplanation}`} /></div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-amber-800">{dashboard.converted_yearly_usd_total == null ? '—' : formatMoney(dashboard.converted_yearly_usd_total, 'USD')}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-2">
          <div className="flex items-center justify-between gap-1 text-[11px] text-emerald-700">{t('maintenance.cards.yearlyTotalTry')}<SummaryInfo label={t('maintenance.cards.yearlyTotalTry')} text={`Yıllık toplam: TL bedelleri + (USD bedelleri × TCMB USD satış kuru). ${rateExplanation}`} /></div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-emerald-800">{dashboard.converted_yearly_try_total == null ? '—' : formatMoney(dashboard.converted_yearly_try_total, 'TRY')}</div>
        </div>
      </div>

        <div className="maintenance-grid min-h-0 flex-1">
            <AgGridReact<MaintenanceContractRow>
              modules={modules}
              theme={maintenanceGridTheme}
              rowData={rows}
              getRowId={params => params.data.uuid_id}
              grandTotalRow="pinnedBottom"
              suppressAggFuncInHeader
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              alwaysShowHorizontalScroll
              loading={loading}
              animateRows
              getRowStyle={({ node }) => ({ fontWeight: node.footer ? '600' : '400', backgroundColor: node.footer ? '#f1f5f9' : 'transparent', cursor: node.footer ? 'default' : 'pointer' })}
              onRowClicked={(event) => {
                if (!event.node.footer && event.data?.uuid_id) {
                  void openEdit(event.data.uuid_id);
                }
              }}
              overlayNoRowsTemplate={`<span class="text-sm text-slate-500">${t('maintenance.empty')}</span>`}
              enableCellTextSelection
            />
        </div>
      </section>

      <ContractModal
        open={modalOpen}
        detail={activeDetail}
        lookups={lookups}
        saving={saving}
        onClose={() => {
          if (saving) return;
          setModalOpen(false);
          setActiveDetail(null);
        }}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />

      {detailLoading && modalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/20 text-sm font-medium text-white">
          {t('common.loading')}
        </div>
      )}
    </div>
  );
}
